import * as React from 'react'
import {ReactElement} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {NatResidentApplicationHistoryInfoComponent} from '../common/resident/NatResidentApplicationHistoryInfoComponent'
import styled from 'styled-components'
import {NatContactAddressOnEmergencyDetail, NatFamilyStructure} from '../repository/NatGetContactAddressOnEmergency'
import {FullWidthColumnFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatGetApplicationResultDetail} from '../repository/NatGetApplicationResult'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import NatResidentContactAddressOnEmergencyConfirm from './resident/NatResidentContactAddressOnEmergencyConfirm'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {NatFamilyStructure as FamilyStructureDetail} from './NatResidentApplicationInterface'
import moment from 'moment'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {getStringOrEmptyString} from '../../../common/util/Natutil'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface States {
    contactAddressOnEmergencyDetail: NatContactAddressOnEmergencyDetail
    applicationResultDetail: NatGetApplicationResultDetail
    isLoadCompleted: boolean
}

class NatResidentContactAddressOnEmergencyPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            contactAddressOnEmergencyDetail: {
                applyId: '',
                applyStatus: '',
                condominiumName: '',
                ridgeName: '',
                roomNumber: '',
                applicantFamilyName: '',
                applicantFirstName: '',
                applyDate: '',
                residenceStyle: '',
                occupancyDate: '',
                contactAddressOnEmergency: {
                    familyName: '',
                    firstName: '',
                    zipCode: '',
                    prefecture: '',
                    address2: '',
                    address3: '',
                    buildingRoomNumber: '',
                    fixedPhoneNumber: '',
                    mobilePhoneNumber: '',
                },
                absenceContactAddress: {
                    familyName: '',
                    firstName: '',
                    zipCode: '',
                    prefecture: '',
                    address2: '',
                    address3: '',
                    buildingRoomNumber: '',
                    fixedPhoneNumber: '',
                    mobilePhoneNumber: '',
                },
                rentAdminContactAddressFlag: '',
                rentAdminContactAddress: {
                    name: '',
                    zipCode: '',
                    prefecture: '',
                    address2: '',
                    address3: '',
                    buildingRoomNumber: '',
                    phoneNumber: '',
                },
                familyStructure: [],
                version: 0,
            },
            applicationResultDetail: {
                applyInfo: {
                    applyId: '',
                    applyCategory: '',
                    ridgeName: '',
                    roomNumber: '',
                    familyName: '',
                    firstName: '',
                    registerDatetime: '',
                },
                resultInfo: {
                    applyStatus: '',
                    roleId: '',
                    comment: '',
                    registerDatetime: '',
                },
            },
            isLoadCompleted: false,
        }

        this.onClickReApplyButton = this.onClickReApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        const [contactAddressOnEmergencyResponse, applicationStatusResponse] = await Promise.all([
            this.getContactAddressOnEmergency(),
            this.getApplicationResult(),
        ])

        if (contactAddressOnEmergencyResponse && applicationStatusResponse) {
            this.setState({
                contactAddressOnEmergencyDetail: contactAddressOnEmergencyResponse,
                applicationResultDetail: applicationStatusResponse,
            })
        }
    }

    async getContactAddressOnEmergency(): Promise<Nullable<NatContactAddressOnEmergencyDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getContactAddressOnEmergencyForResident.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        if (!response) {
            return null
        }

        const convertFamilyStructure: NatFamilyStructure[]
            = response.details.familyStructure.map(family => {
            return {
                seq: family.seq,
                relationship: getStringOrEmptyString(family.relationship),
                familyName: getStringOrEmptyString(family.familyName),
                firstName: getStringOrEmptyString(family.firstName),
                familyNameKana: getStringOrEmptyString(family.familyNameKana),
                firstNameKana: getStringOrEmptyString(family.firstNameKana),
                birthDate: getStringOrEmptyString(family.birthDate),
                workAddress: {
                    name: getStringOrEmptyString(family.workAddress.name),
                    zipCode: getStringOrEmptyString(family.workAddress.zipCode),
                    prefecture: getStringOrEmptyString(family.workAddress.prefecture),
                    address2: getStringOrEmptyString(family.workAddress.address2),
                    address3: getStringOrEmptyString(family.workAddress.address3),
                    buildingRoomNumber: getStringOrEmptyString(family.workAddress.buildingRoomNumber),
                    phoneNumber: getStringOrEmptyString(family.workAddress.phoneNumber),
                },
            }
        })

        const convertResponse: Nullable<NatContactAddressOnEmergencyDetail> = {
            applyId: getStringOrEmptyString(response.details.applyId),
            applyStatus: getStringOrEmptyString(response.details.applyStatus),
            condominiumName: getStringOrEmptyString(response.details.condominiumName),
            ridgeName: getStringOrEmptyString(response.details.ridgeName),
            roomNumber: getStringOrEmptyString(response.details.roomNumber),
            applicantFamilyName: getStringOrEmptyString(response.details.applicantFamilyName),
            applicantFirstName: getStringOrEmptyString(response.details.applicantFirstName),
            applyDate: getStringOrEmptyString(response.details.applyDate),
            residenceStyle: getStringOrEmptyString(response.details.residenceStyle),
            occupancyDate: getStringOrEmptyString(response.details.occupancyDate),
            contactAddressOnEmergency: {
                familyName: getStringOrEmptyString(response.details.contactAddressOnEmergency.familyName),
                firstName: getStringOrEmptyString(response.details.contactAddressOnEmergency.firstName),
                zipCode: getStringOrEmptyString(response.details.contactAddressOnEmergency.zipCode),
                prefecture: getStringOrEmptyString(response.details.contactAddressOnEmergency.prefecture),
                address2: getStringOrEmptyString(response.details.contactAddressOnEmergency.address2),
                address3: getStringOrEmptyString(response.details.contactAddressOnEmergency.address3),
                buildingRoomNumber: getStringOrEmptyString(
                    response.details.contactAddressOnEmergency.buildingRoomNumber),
                fixedPhoneNumber: getStringOrEmptyString(
                    response.details.contactAddressOnEmergency.fixedPhoneNumber),
                mobilePhoneNumber: getStringOrEmptyString(
                    response.details.contactAddressOnEmergency.mobilePhoneNumber),
            },
            absenceContactAddress: {
                familyName: getStringOrEmptyString(response.details.absenceContactAddress.familyName),
                firstName: getStringOrEmptyString(response.details.absenceContactAddress.firstName),
                zipCode: getStringOrEmptyString(response.details.absenceContactAddress.zipCode),
                prefecture: getStringOrEmptyString(response.details.absenceContactAddress.prefecture),
                address2: getStringOrEmptyString(response.details.absenceContactAddress.address2),
                address3: getStringOrEmptyString(response.details.absenceContactAddress.address3),
                buildingRoomNumber: getStringOrEmptyString(
                    response.details.absenceContactAddress.buildingRoomNumber),
                fixedPhoneNumber: getStringOrEmptyString(response.details.absenceContactAddress.fixedPhoneNumber),
                mobilePhoneNumber: getStringOrEmptyString(response.details.absenceContactAddress.mobilePhoneNumber),
            },
            rentAdminContactAddressFlag: getStringOrEmptyString(response.details.rentAdminContactAddressFlag),
            rentAdminContactAddress: {
                name: getStringOrEmptyString(response.details.rentAdminContactAddress.name),
                zipCode: getStringOrEmptyString(response.details.rentAdminContactAddress.zipCode),
                prefecture: getStringOrEmptyString(response.details.rentAdminContactAddress.prefecture),
                address2: getStringOrEmptyString(response.details.rentAdminContactAddress.address2),
                address3: getStringOrEmptyString(response.details.rentAdminContactAddress.address3),
                buildingRoomNumber: getStringOrEmptyString(
                    response.details.rentAdminContactAddress.buildingRoomNumber),
                phoneNumber: getStringOrEmptyString(response.details.rentAdminContactAddress.phoneNumber),
            },
            familyStructure: convertFamilyStructure,
            version: response.details.version,
        }

        return convertResponse
    }

    async getApplicationResult(): Promise<Nullable<NatGetApplicationResultDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplyResult.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    onClickReApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/${this.props.applyId}/copy`,
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        const familyStructureDetail: FamilyStructureDetail[]
            = this.state.contactAddressOnEmergencyDetail.familyStructure.map((family, index) => {
            return {
                index: index,
                relationship: family.relationship,
                familyName: family.familyName,
                firstName: family.firstName,
                familyNameKana: family.familyNameKana,
                firstNameKana: family.firstNameKana,
                birthDate: family.birthDate,
                birthYear: family.birthDate ? moment(family.birthDate).year().toString() : '',
                birthMonth: family.birthDate ? (moment(family.birthDate).month() + 1).toString() : '',
                birthDay: family.birthDate ? moment(family.birthDate).date().toString() : '',
                workAddress: {
                    name: family.workAddress.name,
                    zipCode: family.workAddress.zipCode,
                    prefecture: family.workAddress.prefecture,
                    address2: family.workAddress.address2,
                    address3: family.workAddress.address3,
                    buildingRoomNumber: family.workAddress.buildingRoomNumber,
                    phoneNumber: family.workAddress.phoneNumber,
                },
            }
        })

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentApplicationHistoryInfoComponent
                    applyId={this.props.applyId}
                    applyHistoryDivision={convertApplyStatusIntoApplyHistoryDivision(
                        this.state.contactAddressOnEmergencyDetail.applyStatus)}
                    applyResultInfo={this.state.applicationResultDetail}
                />
                <NatResidentContactAddressOnEmergencyConfirm
                    {...this.state.contactAddressOnEmergencyDetail}
                    familyStructureList={familyStructureDetail}
                    applyFlag={false}
                />
                <AuthorityChecker permittedAuthorities={
                    HKConstants.CMN.AUTH.APPLY_CONTACT_ADDRESS_EMERGENCY}>
                    {(this.state.contactAddressOnEmergencyDetail.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
                            this.state.contactAddressOnEmergencyDetail.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) &&
                        <ReApplyButton
                            onClick={this.onClickReApplyButton}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    内容を編集して再申請する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </ReApplyButton>
                    }
                </AuthorityChecker>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentContactAddressOnEmergencyPage)

export const ReApplyButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`
